import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { publicAssetUrl } from "../../lib/adminApi";

function formatDate(value) {
  if (!value) return "Recently published";
  return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function splitKeywords(value) {
  return String(value || "")
    .split(/[,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function JournalArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await api(`/api/journal-articles/${slug}`);
        setArticle(data);
      } catch (err) {
        setError(err.message);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  async function countDownload() {
    if (!article?.id) return;
    try {
      await api(`/api/journal-articles/${article.id}/count`, { method: "POST" });
    } catch {
      // Do not block downloads if count fails.
    }
  }

  if (loading) return <main className="min-h-screen bg-slate-50 p-8">Loading article...</main>;
  if (error || !article) {
    return (
      <main className="min-h-screen bg-slate-50 p-8">
        <div className="mx-auto max-w-3xl rounded-3xl bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-black text-slate-950">Article not found</h1>
          <p className="mt-3 text-slate-600">{error || "The journal article could not be loaded."}</p>
          <Link to="/journals" className="mt-5 inline-block font-black text-emerald-700">Back to journals</Link>
        </div>
      </main>
    );
  }

  const keywords = splitKeywords(article.keywords);

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-12">
      <article className="mx-auto max-w-4xl rounded-[2rem] bg-white p-6 shadow-sm ring-1 ring-slate-200 md:p-10">
        <Link to={article.issue_slug ? `/journals/${article.issue_slug}` : "/journals"} className="font-black text-emerald-700">← Back to issue</Link>

        <div className="mt-6 flex flex-wrap gap-2">
          <span className="rounded-full bg-emerald-100 px-4 py-2 text-xs font-black uppercase tracking-wider text-emerald-800">Journal Article</span>
          {article.pages && <span className="rounded-full bg-slate-100 px-4 py-2 text-xs font-black text-slate-700">Pages: {article.pages}</span>}
          {Number(article.is_featured) === 1 && <span className="rounded-full bg-yellow-100 px-4 py-2 text-xs font-black text-yellow-800">Featured</span>}
        </div>

        <h1 className="mt-5 text-3xl font-black leading-tight text-slate-950 md:text-5xl">{article.title}</h1>
        <p className="mt-5 text-lg font-semibold text-slate-700">{article.authors}</p>
        <p className="mt-2 text-sm text-slate-500">
          {article.issue_title || "Journal issue"} • {article.volume || "Volume"} • {article.issue || "Issue"} • {formatDate(article.published_at || article.publication_date)}
        </p>

        {article.doi && <p className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-700">DOI: {article.doi}</p>}

        <section className="mt-8">
          <h2 className="text-xl font-black text-slate-950">Abstract</h2>
          <p className="mt-3 whitespace-pre-line leading-8 text-slate-700">{article.abstract || "No abstract has been added for this article yet."}</p>
        </section>

        {keywords.length > 0 && (
          <section className="mt-8">
            <h2 className="text-xl font-black text-slate-950">Keywords</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {keywords.map((keyword) => (
                <span key={keyword} className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700">{keyword}</span>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 flex flex-col gap-3 rounded-3xl bg-slate-50 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-bold text-slate-500">Downloads</p>
            <p className="text-2xl font-black text-slate-950">{article.download_count || 0}</p>
          </div>
          {article.pdf_url ? (
            <a
              href={publicAssetUrl(article.pdf_url)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={countDownload}
              className="rounded-2xl bg-emerald-700 px-6 py-4 text-center font-black text-white"
            >
              Download PDF
            </a>
          ) : (
            <span className="rounded-2xl bg-slate-200 px-6 py-4 text-center font-black text-slate-600">PDF not uploaded</span>
          )}
        </div>
      </article>
    </main>
  );
}
